import moment from 'moment'

import { getIsoDayOfWeek } from 'utils/days'

/**
 * @param  {string} timestamp Time to set in the format of HH:mm
 * @param  {moment} date  Moment to set time on
 *
 * @return {moment} A new moment with the time set
 */
function setTimeOnDate(timestamp, date) {
  const time = moment(timestamp, 'HH:mm')
  return date.clone().set({
    hour: time.get('hour'),
    minute: time.get('minute')
  })
}

/**
 * @param  {string} timestamp Time to set in the format of HH:mm
 * @param  {string} timezone
 *
 * @return {moment} A new moment with the time set based on the timezone
 */
function fromTimezone(timestamp, timezone) {
  return setTimeOnDate(timestamp, moment.tz(moment(), timezone))
}

/**
 * @param  {moment} timestamp Moment where the time has been set
 * @param  {string} timezone
 *
 * @return {string} Time on the timestamp converted to the given the provided timezone
 */
function toTimezone(timestamp, timezone) {
  return timestamp.clone().tz(timezone).format('HH:mm')
}

/**
 * @param  {string} day Day of the week
 * @param  {string} timezone
 *
 * @return {moment} Next instance of the given day of the week for the given timezone
 */
function getNextDayOfWeekForTimezone(day, timezone) {
  const dayOfWeek = getIsoDayOfWeek(day)
  const localizedTime = moment.tz(moment(), timezone)
  const today = localizedTime.isoWeekday()
  if (today <= dayOfWeek) {
    return localizedTime.isoWeekday(dayOfWeek)
  } else {
    return localizedTime.add(1, 'weeks').isoWeekday(dayOfWeek)
  }
}

/**
 * Returns an object based on the type with properties to display the schedule
 * @param  {object} trigger
 *
 * @return {object} start/end for Random. date/time for Once
 */
export function parseTriggerForDisplay(trigger) {
  switch (trigger.type) {
    case 'Random': {
      const startTz = fromTimezone(trigger.start, trigger.timeZone)
      const endTz = fromTimezone(trigger.end, trigger.timeZone)
      const localTz = moment.tz.guess()
      return {
        start: toTimezone(startTz, localTz),
        end: toTimezone(endTz, localTz)
      }
    }
    case 'Once':
    default:
      return {
        date: moment(trigger.exact).format('MMM D Y'),
        time: moment(trigger.exact).format('HH:mm')
      }
  }
}

/**
 * Returns the next execution of the trigger based on the start
 * @param  {object} trigger
 *
 * @return {string} ISO string of the next execution window
 */
export function nextExecution(trigger) {
  switch (trigger.type) {
    case 'Random':
      if (trigger.activeDays.length === 0) {
        return null
      }
      return trigger.activeDays
        .map((day) =>
          setTimeOnDate(
            trigger.start,
            getNextDayOfWeekForTimezone(day, trigger.timeZone)
          ).toISOString()
        )
        .sort()[0]
    case 'Once':
    default:
      return trigger.exact
  }
}

/**
 * Returns whether the trigger is currently within the execution window
 * @param  {object} trigger
 * @param  {date} [date=now] The time to test if within the window
 *
 * @return {bool} Within execution window or not
 */
export function isWithinWindow(trigger, date) {
  const { start, end, timeZone, activeDays, type } = trigger
  if (type !== 'Random') {
    return false
  }
  const daysToExecute = activeDays.map((day) => getIsoDayOfWeek(day))
  const localizedTime = moment.tz(moment(date), timeZone)
  const currentDayOfWeek = localizedTime.isoWeekday()

  if (daysToExecute.includes(currentDayOfWeek)) {
    const startDate = setTimeOnDate(start, localizedTime)
    const endDate = setTimeOnDate(end, localizedTime)
    return moment(localizedTime).isBetween(startDate, endDate)
  }
  return false
}

/**
 * Returns whether the trigger will run within the given range
 * @param  {object} trigger
 * @param  {date} start
 * @param  {date} end
 *
 * @return {bool} Within execution window or not
 */
export const willTriggerRunWithin = (trigger, start, end) => {
  const nextRun = nextExecution(trigger)
  if (nextRun === null) {
    return false
  }
  switch (trigger.type) {
    case 'Random':
      if (isWithinWindow(trigger, start)) {
        return true
      }
      return moment(nextRun).isBetween(start, end)
    case 'Once':
    default:
      return moment(nextRun).isBetween(start, end)
  }
}
